import { ReactiveMap } from "@solid-primitives/map";
import { ReactiveSet } from "@solid-primitives/set";
import type {
	Channel,
	ChannelCreate,
	ChannelPatch,
	Tag,
	TagCreate,
	TagPatch,
} from "sdk";
import { batch } from "solid-js";
import { BaseService } from "../core/Service";

export class ChannelsService extends BaseService<Channel> {
	protected cacheName = "channel";

	private channelsByRoom = new ReactiveMap<
		string | null,
		ReactiveSet<string>
	>();

	getKey(item: Channel): string {
		return item.id;
	}

	protected override afterUpsert(channel: Channel) {
		const roomId = channel.room_id ?? null;
		const r = this.channelsByRoom.get(roomId) ?? new ReactiveSet();
		r.add(channel.id);
		this.channelsByRoom.set(roomId, r);
	}

	protected override afterUpsertBulk(channels: Channel[]) {
		const byRoom = new Map<string | null, Set<string>>();
		for (const c of channels) {
			const roomId = c.room_id ?? null;
			let s = byRoom.get(roomId);
			if (!s) {
				s = new Set();
				byRoom.set(roomId, s);
			}
			s.add(c.id);
		}

		batch(() => {
			for (const [room_id, channel_ids] of byRoom) {
				const r = this.channelsByRoom.get(room_id) ?? new ReactiveSet();
				for (const id of channel_ids) {
					r.add(id);
				}
				this.channelsByRoom.set(room_id, r);
			}
		});
	}

	protected override afterDelete(channel_id: string, channel?: Channel) {
		if (channel) {
			const roomId = channel.room_id ?? null;
			this.channelsByRoom.get(roomId)?.delete(channel_id);
		}
	}

	listByRoom(room_id: string | null): Channel[] {
		const ids = this.channelsByRoom.get(room_id);
		if (!ids) return [];
		return [...ids]
			.map((id) => this.cache.get(id))
			.filter((c): c is Channel => c != null);
	}

	async fetch(id: string): Promise<Channel> {
		return await this.retryWithBackoff<Channel>(() =>
			this.client.http.GET("/api/v1/channel/{channel_id}", {
				params: { path: { channel_id: id } },
			}),
		);
	}

	async fetchRoomList(room_id: string): Promise<Channel[]> {
		if (this.channelsByRoom.has(room_id)) {
			return this.listByRoom(room_id);
		}

		const channels = await this.retryWithBackoff(() =>
			this.client.http.GET("/api/v1/room/{room_id}/channel", {
				params: { path: { room_id } },
			}),
		);
		this.upsertBulk(channels.items);
		return channels.items;
	}

	async create(room_id: string, body: ChannelCreate): Promise<Channel> {
		const data = await this.retryWithBackoff<Channel>(() =>
			this.client.http.POST("/api/v1/room/{room_id}/channel", {
				params: { path: { room_id } },
				body,
			}),
		);
		this.upsert(data);
		return data;
	}

	async update(channel_id: string, body: ChannelPatch): Promise<Channel> {
		const data = await this.retryWithBackoff<Channel>(() =>
			this.client.http.PATCH("/api/v1/channel/{channel_id}", {
				params: { path: { channel_id } },
				body,
			}),
		);
		this.upsert(data);
		return data;
	}

	async typing(channel_id: string): Promise<void> {
		await this.retryWithBackoff(() =>
			this.client.http.POST("/api/v1/channel/{channel_id}/typing", {
				params: { path: { channel_id } },
			}),
		);
	}

	// TODO: copy this logic to ackBulk
	async ack(
		channel_id: string,
		message_id: string | undefined,
		version_id: string | undefined,
	): Promise<void> {
		const data = await this.retryWithBackoff(() =>
			this.client.http.PUT("/api/v1/channel/{channel_id}/ack", {
				params: { path: { channel_id } },
				body: { message_id, version_id },
			}),
		);

		if (data.type !== "Message")
			throw new Error("got incorrect Ack type; expected Message ack");

		// Update cache
		const t = this.cache.get(channel_id);
		if (t) {
			this.cache.set(channel_id, {
				...t,
				last_read_id: data.message_id,
				mention_count: data.mention_count ?? 0,
				is_unread: data.unread,
			} as Channel);
		}
	}

	async ackBulk(
		acks: Array<{
			channel_id: string;
			message_id?: string;
			version_id: string;
			mention_count?: number;
		}>,
	): Promise<void> {
		await this.retryWithBackoff(() =>
			this.client.http.POST("/api/v1/ack", {
				body: { acks },
			}),
		);

		// Update cache in batch
		for (const ack of acks) {
			const t = this.cache.get(ack.channel_id);
			if (t) {
				const is_unread = ack.version_id < (t.last_version_id ?? "");
				const mention_count = ack.mention_count ?? 0;
				this.cache.set(ack.channel_id, {
					...t,
					last_read_id: ack.version_id,
					mention_count,
					is_unread,
				} as Channel);
			}
		}
	}

	async archive(channel_id: string): Promise<Channel> {
		return await this.update(channel_id, { archived: true });
	}

	async unarchive(channel_id: string): Promise<Channel> {
		return await this.update(channel_id, { archived: false });
	}

	async lock(channel_id: string): Promise<Channel> {
		return await this.update(channel_id, { locked: { allow_roles: [] } });
	}

	async unlock(channel_id: string): Promise<Channel> {
		return await this.update(channel_id, { locked: null });
	}

	async createThreadFromMessage(
		channel_id: string,
		message_id: string,
		body: ChannelCreate,
	): Promise<Channel> {
		const data = await this.retryWithBackoff<Channel>(() =>
			this.client.http.POST(
				"/api/v1/channel/{channel_id}/message/{message_id}/thread",
				{
					params: { path: { channel_id, message_id } },
					body,
				},
			),
		);
		this.upsert(data);
		return data;
	}

	async createTag(channel_id: string, body: TagCreate): Promise<Tag> {
		return await this.retryWithBackoff(() =>
			this.client.http.POST("/api/v1/channel/{channel_id}/tag", {
				params: { path: { channel_id } },
				body: body,
			}),
		);
	}

	async updateTag(
		channel_id: string,
		tag_id: string,
		body: TagPatch,
	): Promise<Tag> {
		return await this.retryWithBackoff(() =>
			this.client.http.PATCH("/api/v1/channel/{channel_id}/tag/{tag_id}", {
				params: { path: { channel_id, tag_id } },
				body: body,
			}),
		);
	}

	async deleteTag(
		channel_id: string,
		tag_id: string,
		force: boolean = false,
	): Promise<void> {
		await this.retryWithBackoff(() =>
			this.client.http.DELETE("/api/v1/channel/{channel_id}/tag/{tag_id}", {
				params: { path: { channel_id, tag_id } },
				query: { force },
			}),
		);
	}

	// Helper normalization method
	protected override prepareUpsert(channel: Channel): Channel {
		if (!channel.permission_overwrites) channel.permission_overwrites = [];
		if (!channel.recipients) channel.recipients = [];
		if (
			!channel.tags &&
			(channel.type === "ThreadPublic" ||
				channel.type === "ThreadPrivate" ||
				channel.type === "ThreadForum2")
		) {
			channel.tags = [];
		}
		return channel;
	}

	clear() {
		super.clear();
		for (const r of this.channelsByRoom.values()) {
			r.clear();
		}
		this.channelsByRoom.clear();
	}
}
